import { useState, useEffect } from 'react';
import { Dispatch, SetStateAction } from 'react';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import { Modal, Button } from '@mui/material';
import { showError, showToast } from '../constants/toasts';
import { useAddFAQMutation, useUpdateFaqMutation } from '../services/cms';

interface ModalProps {
    open: boolean;
    onClose: () => void;
    setOpen: Dispatch<SetStateAction<boolean>>;
    editData?: any;
    isEdit?: boolean;
    onSuccess?: () => void;
}

export default function AddFaq({ open, onClose, setOpen, editData, isEdit = false, onSuccess }: ModalProps) {
    const [question, setQuestion] = useState("");
    const [answer, setAnswer] = useState("");
    const [error, setError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [questionError, setQuestionError] = useState("");
    const [answerError, setAnswerError] = useState("");

    const [addFAQ] = useAddFAQMutation();
    const [updateFAQ] = useUpdateFaqMutation();

    useEffect(() => {
        if (editData && isEdit) {
            // Handle both direct string fields and nested object fields
            const questionText = typeof editData.question === 'string' 
                ? editData.question 
                : editData.question?.en || '';
            const answerText = typeof editData.answer === 'string' 
                ? editData.answer 
                : editData.answer?.en || '';
            
            setQuestion(questionText);
            setAnswer(answerText);
        } else {
            setQuestion('');
            setAnswer('');
        }
        // Clear errors when modal opens
        setQuestionError("");
        setAnswerError("");
        setError(false);
    }, [editData, isEdit, open]);

    const validateQuestion = (value: string) => {
        if (!value || value.trim() === "") {
            return "Question is required";
        }
        if (value.trim().length > 100) {
            return "Question cannot exceed 100 characters";
        }
        return "";
    };

    const validateAnswer = (value: string) => {
        if (!value || value.trim() === "") {
            return "Answer is required";
        }
        if (value.trim().length > 250) {
            return "Answer cannot exceed 250 characters";
        }
        return "";
    };

    const handleQuestionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setQuestion(value);
        setQuestionError(validateQuestion(value));
    };

    const handleAnswerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setAnswer(value);
        setAnswerError(validateAnswer(value));
    };

    const handleSubmit = async () => {
        setError(true);
        
        // Validate both fields
        const questionValidation = validateQuestion(question);
        const answerValidation = validateAnswer(answer);
        
        setQuestionError(questionValidation);
        setAnswerError(answerValidation);
        
        if (questionValidation || answerValidation) {
            return;
        }

        setIsLoading(true);
        try {
            const body = {
                question: question.trim(),
                answer: answer.trim(),
            };

            if (isEdit && (editData?._id || editData?.id)) {
                const response = await updateFAQ({ 
                    body: body, 
                    id: editData._id || editData.id 
                }).unwrap();
                if (response?.status === 200 || response?.status === 201) {
                    showToast('FAQ updated successfully');
                    onSuccess?.();
                    setOpen(false);
                }
            } else {
                const response = await addFAQ({ body }).unwrap();
                if (response?.status === 200 || response?.status === 201) {
                    showToast('FAQ added successfully');
                    onSuccess?.();
                    setOpen(false);
                }
            }
        } catch (error: any) {
            showError(error?.data?.message || 'Something went wrong');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            className="modal addNotification_modal"
            open={open}
            onClose={() => {
                onClose();
                setError(false);
                setQuestion("");
                setAnswer("");
                setQuestionError("");
                setAnswerError("");
            }}
        >
            <div className="modal-dialog">
                <div className="modal-body">
                    <div className="modal_title">
                        <h2>{isEdit ? 'Edit FAQ' : 'Add FAQ'}</h2>
                        <div className="btn-close" onClick={() => {
                            setOpen(false);
                            setQuestion("");
                            setAnswer("");
                            setQuestionError("");
                            setAnswerError("");
                        }}>
                            <CloseIcon />
                        </div>
                    </div>
                    <form className="form" action="">
                        <div className="control_group">
                            <TextField
                                variant="outlined"
                                placeholder="Question (max 100 characters)"
                                fullWidth
                                value={question}
                                onChange={handleQuestionChange}
                                hiddenLabel
                                error={Boolean(questionError)}
                                helperText={questionError}
                                inputProps={{
                                    maxLength: 100
                                }}
                            />
                            <div style={{
                                textAlign: 'right',
                                fontSize: '12px',
                                color: question.length > 90 ? '#f44336' : '#666',
                                marginTop: '4px',
                                fontStyle: 'italic'
                            }}>
                              
                            </div>
                        </div>
                        <div className="control_group">
                            <TextField
                                variant="outlined"
                                placeholder="Answer (max 250 characters)"
                                fullWidth
                                hiddenLabel
                                multiline
                                value={answer}
                                onChange={handleAnswerChange}
                                maxRows={5}
                                minRows={5}
                                error={Boolean(answerError)}
                                helperText={answerError}
                                inputProps={{
                                    maxLength: 250
                                }}
                            />
                            <div style={{
                                textAlign: 'right',
                                fontSize: '12px',
                                color: answer.length > 225 ? '#f44336' : '#666',
                                marginTop: '4px',
                                fontStyle: 'italic'
                            }}>
                              
                            </div>
                        </div>
                        <div className="footer_btn_flex">
                            <Button 
                                className="btn btn_grey" 
                                onClick={() => {
                                    setError(false);
                                    setOpen(false);
                                    setQuestion("");
                                    setAnswer("");
                                    setQuestionError("");
                                    setAnswerError("");
                                }}
                                disabled={isLoading}
                            >
                                Cancel
                            </Button>
                            <Button 
                                className="btn btn_primary" 
                                onClick={handleSubmit}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Processing...' : (isEdit ? 'Update' : 'Submit')}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </Modal >
    )
}
